import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Users, UserCheck, UserX, Clock, GraduationCap, School } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, sessionToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600 font-medium">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 font-heading mb-2" data-testid="dashboard-title">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-slate-500 text-lg">Here's what's happening today</p>
        </div>

        {/* Admin Dashboard */}
        {user?.role === 'admin' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 rounded-2xl border-slate-100 hover-lift bg-white" data-testid="total-students-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Total Students</p>
                  <h3 className="text-3xl font-bold text-slate-900">{stats.total_students}</h3>
                </div>
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-sky-500" />
                </div>
              </div>
            </Card>

            <Card className="p-6 rounded-2xl border-slate-100 hover-lift bg-white" data-testid="total-classes-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Total Classes</p>
                  <h3 className="text-3xl font-bold text-slate-900">{stats.total_classes}</h3>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <School className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </Card>

            <Card className="p-6 rounded-2xl border-slate-100 hover-lift bg-white" data-testid="present-today-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Present Today</p>
                  <h3 className="text-3xl font-bold text-green-600">{stats.present_count}</h3>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </Card>

            <Card className="p-6 rounded-2xl border-slate-100 hover-lift bg-white" data-testid="absent-today-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Absent Today</p>
                  <h3 className="text-3xl font-bold text-red-600">{stats.absent_count}</h3>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <UserX className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Teacher Dashboard */}
        {user?.role === 'teacher' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 rounded-2xl border-slate-100 hover-lift bg-white" data-testid="teacher-total-students">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">My Students</p>
                  <h3 className="text-3xl font-bold text-slate-900">{stats.total_students}</h3>
                </div>
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-sky-500" />
                </div>
              </div>
            </Card>

            <Card className="p-6 rounded-2xl border-slate-100 hover-lift bg-white" data-testid="teacher-present">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Present Today</p>
                  <h3 className="text-3xl font-bold text-green-600">{stats.present_count}</h3>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </Card>

            <Card className="p-6 rounded-2xl border-slate-100 hover-lift bg-white" data-testid="teacher-absent">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Absent Today</p>
                  <h3 className="text-3xl font-bold text-red-600">{stats.absent_count}</h3>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <UserX className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </Card>

            <Card className="p-6 rounded-2xl border-slate-100 hover-lift bg-white" data-testid="teacher-late">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Late Today</p>
                  <h3 className="text-3xl font-bold text-amber-600">{stats.late_count}</h3>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Parent Dashboard */}
        {user?.role === 'parent' && stats?.children && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900 font-heading">My Children</h2>
            {stats.children.length === 0 ? (
              <Card className="p-8 rounded-2xl border-slate-100 bg-white text-center">
                <p className="text-slate-500">No children linked to your account yet.</p>
                <p className="text-sm text-slate-400 mt-2">Please contact the school administration.</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {stats.children.map((child, index) => (
                  <Card key={index} className="p-6 rounded-2xl border-slate-100 hover-lift bg-white" data-testid={`child-card-${index}`}>
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-sky-600 font-semibold text-lg">{child.name.charAt(0)}</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">{child.name}</h3>
                        <p className="text-sm text-slate-500">Roll No: {child.roll_no}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Attendance</span>
                        <span className={`text-2xl font-bold ${child.attendance_percentage >= 90 ? 'text-green-600' : child.attendance_percentage >= 75 ? 'text-blue-600' : 'text-red-600'}`}>
                          {child.attendance_percentage}%
                        </span>
                      </div>
                      <div className="mt-2 w-full bg-slate-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${child.attendance_percentage >= 90 ? 'bg-green-500' : child.attendance_percentage >= 75 ? 'bg-blue-500' : 'bg-red-500'}`}
                          style={{ width: `${child.attendance_percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
